import { Lightning, Lightning as lng, Utils } from '@lightningjs/sdk';
import { ScrollList } from '../../../components/shared/ScrollList';
import { MainMenuItem } from './MainMenuItem';
import { ProfileMenu } from './ProfileMenu';
import { MenuData } from '../api/MenuData';
import { PageDetector } from '../api/PageDetector';
import { GlobalSettingsKeys } from '../../../api/models/GlobalSettingsKeys';

const Constants = {
  w: 1027,
  top: {
    x: 0,
    y: 0,
    h: 200,
  },
  bottom: {
    x: 25,
    y: 1080 - 30,
    h: 60,
    w: 300,
  },
};

export class MainMenu extends lng.Component {
  static _template() {
    return {
      x: 0,
      y: 0,
      w: w => w,
      h: h => h,
      Background: {
        type: lng.Component,
        x: 0,
        y: 0,
        h: h => h,
        w: w => w,
        // color: Theme.Colors.Transparent,
        src: Utils.asset('images/menu/background.png'),
        // rect: true,
      },
      ItemList: {
        y: Constants.top.y + Constants.top.h,
        h: Constants.bottom.y - Constants.top.h,
        w: w => w,
        x: 0,
        clipping: true,
        type: ScrollList,
        Items: {
          w: w => w,
        },
        signals: {
          onActivate: 'menuItemActivated',
        },
      },
      Profile: {
        type: ProfileMenu,
        x: 0,
        y: Constants.top.y,
        w: w => w,
        h: Constants.top.h,
        visible: false,
      },
      Logo: {
        x: Constants.bottom.x,
        y: Constants.bottom.y,
        mountY: 1,
        texture: {
          type: Lightning.textures.ImageTexture,
          src: Utils.asset('images/logo_transparent.png'),
          resizeMode: {
            type: 'contain',
            h: Constants.bottom.h,
            w: Constants.bottom.w,
          },
        },
      },
    };
  }

  _init() {
    this.focusIndex = -1;
    this.itemStack = [];
  }

  /**
   * Initialize the main menu with the current backend
   * @param {Backend} backend
   */
  initialize(backend) {
    this._currentMenu = undefined;
    this.backend = backend;

    this.menuData = new MenuData(backend);
    this.menuTracker = this.menuData.createMenuTracker();
    this.showMenu(this.menuTracker.activeMenu());
    // check global setting to allow profile switching
    this.allowSwitching = this.backend.globalSettings.boolValue(GlobalSettingsKeys.allowProfileSwitching);
    if (this.allowSwitching) {
      this.tag('Profile').initialize(this.menuData.profileData());
      this.tag('Profile').visible = true;
    }

    return new Promise(resolve => {
      if (this._backgroundLoaded) resolve();
      this.tag('Background').on('txLoaded', () => {
        this._backgroundLoaded = true;
        resolve();
      });
    });
  }

  openDefaultPage() {
    // find the default until it is a page
    let menu = this.menuTracker.activeMenu();
    this._openDefaultSubItem(menu);
  }

  /**
   * Shows the menu
   * @param {MenuInfo} info
   */
  showMenu(info) {
    if (this._currentMenu && this._currentMenu.parent === info.parent) {
      this._currentMenu.active = info.active;
      this._updateActive(info);
    } else {
      this._currentMenu = info;
      this._replaceItems(info);
      this._updateActive(info);
      const index = info.active
        ? info.items.findIndex(nav => nav.tag === info.active.tag)
        : info.items.findIndex(nav => nav.isDefault);
      this.tag('ItemList').focusedIndex = index !== -1 ? index : 0;
    }
  }

  /**
   * Replace the items shown in the menu
   * @param {MenuInfo} info
   * @private
   */
  _replaceItems(info) {
    this.tag('ItemList').clearItems();
    info.items.forEach(item => {
      this.tag('ItemList').items.childList.a({
        type: MainMenuItem,
        label: item.title,
        icon: Utils.asset(`images/menu/${item.icon}.png`),
        item: item,
        passSignals: {
          onActivate: true,
        },
      });
    });
  }

  /**
   * Update the active state of the menu items
   * @param {MenuInfo} info
   * @private
   */
  _updateActive(info) {
    const activeTag = info.active ? info.active.tag : null;
    this.tag('ItemList').items.children.forEach(nav => {
      nav.active = nav.item.tag === activeTag;
    });
  }

  menuItemActivated(navItem) {
    const info = this.menuTracker.selectItem(this._currentMenu, navItem.item);
    this.showMenu(info);
    // console.log(navItem, info, this.menuTracker);
    if (!navItem.item.items) {
      const page = PageDetector.forItem(navItem.item);
      const params = {
        backend: this.backend,
        menuItem: navItem.item,
        name: navItem.item.title,
        properties: navItem.item.properties,
      };
      this.fireAncestors('$openPage', page, params);
      this.fireAncestors('$focusContent');
    } else {
      // its a folder so search if there is an element with default and select else do nothing
      const activeMenu = this.menuTracker.activeMenu();
      this._openDefaultSubItem(activeMenu);
    }
  }

  _openDefaultSubItem(activeMenu) {
    // search if child is a default
    let defaultMenuItem = activeMenu;
    while (defaultMenuItem) {
      // has no subitems, so its a page
      if (!defaultMenuItem.items) {
        const page = PageDetector.forItem(defaultMenuItem);
        const params = {
          backend: this.backend,
          menuItem: defaultMenuItem,
          name: defaultMenuItem.title,
          properties: defaultMenuItem.properties,
        };
        this.fireAncestors('$openPage', page, params);
        break;
      } else {
        // else search for default subitem
        defaultMenuItem = defaultMenuItem.items.find(el => el.isDefault);
      }
    }
  }

  _getFocused() {
    return this.profileHasFocus ? this.tag('Profile') : this.tag('ItemList');
  }

  _handleUp() {
    if (this.allowSwitching) this.profileHasFocus = true;
    return true;
  }

  _handleDown() {
    this.profileHasFocus = false;
    return true;
  }

  _handleBack() {
    const info = this.menuTracker.parentMenu(this._currentMenu);
    if (info) {
      this.showMenu(info);
      return true;
    }
    return false;
  }

  _handleRight() {
    const info = this.menuTracker.activeMenu();
    this.showMenu(info);
    return false;
  }

  _unfocus() {
    this.tag('Background').setSmooth('alpha', 0);
    this.tag('Profile').setSmooth('alpha', 0);
    this.tag('Logo').setSmooth('alpha', 0);
    this.tag('ItemList').items.children.forEach(nav => {
      nav.collapse();
    });
  }

  _focus() {
    this.tag('Background').setSmooth('alpha', 1);
    this.tag('Profile').setSmooth('alpha', 1);
    this.tag('Logo').setSmooth('alpha', 1);
    this.tag('ItemList').items.children.forEach(nav => {
      nav.expand();
    });
  }

  _goUpOneLevel() {
    const info = this.menuTracker.parentMenu(this._currentMenu);
    if (info) {
      this.showMenu(info);
      // this.menuItemActivated({ item: menu[i] });
    }
  }

  updateMenuForPage(pageType) {
    // search current menu
    const activeMenu = this.menuTracker.activeMenu();
    // console.log(activeMenu);
    let found = activeMenu.items.find(item => item.pageType === pageType);
    if (found) {
      const index = activeMenu.items.indexOf(found);
      const info = this.menuTracker.activate(found);
      this._updateActive(info);
      this.tag('ItemList').focusIndex = index;
      return true;
    }

    // check if top level menu contains page
    const menu = this.menuData.menuItems();
    found = menu.find(item => item.pageType === pageType);
    this._goUpOneLevel();
    if (found) {
      const index = menu.indexOf(found);
      const info = this.menuTracker.activate(found);
      this._updateActive(info);
      this.tag('ItemList').focusIndex = index;
      return true;
    } else {
      // find submenu item and top level item
      let i = 0;
      let j;
      for (i; i < menu.length; i++) {
        if (menu[i].items) {
          found = menu[i].items.find(item => item.pageType === pageType);
          if (found) j = menu[i].items.indexOf(found);
        }
        if (found) break;
      }
      if (!found) return false;
      this.menuItemActivated({ item: menu[i] }); // 'buttonpress' on top level menu
      this.tag('ItemList').focusIndex = i;
      const info = this.menuTracker.activate(found); // activate submenu item
      if (info) this.showMenu(info); // update indexes
      this._updateActive(info);
      this.tag('ItemList').focusIndex = j;
      this._unfocus();
      return true;
    }
  }

  selectPageIdInCurrentMenu(id) {
    const activeMenu = this.menuTracker.activeMenu();
    let found = activeMenu.items.find(item => id === item.pageId);

    if (found) {
      // select and open
      const index = activeMenu.items.indexOf(found);
      const info = this.menuTracker.activate(found);
      this._updateActive(info);
      // this.menuItemActivated({ item: found }); // 'buttonpress' on menu item
      const infoNewItem = this.menuTracker.selectItem(this._currentMenu, found);
      this.showMenu(infoNewItem);
      this.tag('ItemList').focusIndex = index;
      return true;
    } else {
      return false;
    }
  }

  searchFullMenuAndSelect(id) {
    // search all and select first
    let initial = this.menuTracker.menuRoot;
    let indexPath = this.searchMenuRecursive(initial.items, id);
    // console.log('[index path]', indexPath, initial);
    if (indexPath.length === 0) return false; // niet gevonden

    //reset tracker to initial menu
    this.menuTracker.resetTrackerToInitial();
    indexPath.forEach(idx => {
      const menu = this.menuTracker.activeMenu();
      const selected = menu.items[idx];
      const info = this.menuTracker.activate(selected);
      this._updateActive(info);
      this.menuItemActivated({ item: selected }); // 'buttonpress' on menu item eerst folders en als laatste het item zelf
      this.tag('ItemList').focusIndex = idx;
    });
    this._unfocus();
    return true;
  }

  /**
   * recursieve functie om te zoeken als er ergens een pagina in een submenu zit. Geeft de indexen terug van het pad naar het menu item
   * @param {IMenuItem[]} menuItems
   * @param {string} id
   * @returns {number[]}
   */
  searchMenuRecursive(menuItems, id) {
    for (let i = 0; i < menuItems.length; i++) {
      // loop through all menu items
      if (menuItems[i].pageId) {
        // menu item is not a folder
        if (menuItems[i].pageId === id) {
          // item found
          return [i];
        }
      }
      if (menuItems[i].items?.length > 0) {
        // menu items has subitems
        let foundArr = this.searchMenuRecursive(menuItems[i].items, id);
        if (foundArr.length > 0) {
          foundArr.unshift(i);
          return foundArr;
        }
      }
    }
    return [];
  }
}
